import { isString } from "@huub-web/utils";

import type { NavigationState, BasNavigationStateRecord } from "../../types";

/**
 * Finds the endpoint for a given path by searching through navigationState.
 * Matches path segments against record slugs and handles dynamic placeholders.
 *
 * @param path - The path to find the endpoint for (e.g., "/woningen", "/woningen/zoeken")
 * @param navigationState - The application state containing all navigation records
 * @returns The matching endpoint or undefined if no match is found
 */
export const getEndpointByPathFromNavigationState = (
  path: string,
  navigationState: NavigationState,
): string | undefined => {
  if (!path || !navigationState || navigationState.length === 0) {
    return undefined;
  }

  // Special case for root path
  if (path === "/" || path === "") {
    const rootRecord = navigationState.find((record) => record.slug === "/");
    return rootRecord && isString(rootRecord.endpoint)
      ? rootRecord.endpoint
      : undefined;
  }

  // Normalize path and split into segments
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  const pathSegments = normalizedPath.split("/").filter(Boolean);

  if (pathSegments.length === 0) {
    return undefined;
  }

  /**
   * Recursively searches for a matching endpoint in the state tree
   */
  const findEndpoint = (
    records: BasNavigationStateRecord[],
    segments: string[],
    parentEndpoint?: string,
    placeholderReplacements: Record<string, string> = {}, // Track replacements
  ): string | undefined => {
    if (!segments.length || !records) {
      return undefined;
    }

    const currentSegment = segments[0];
    const remainingSegments = segments.slice(1);

    // First try to find an exact slug match
    let matchingRecord = records.find((record) => {
      // Normalize slug by removing leading slash if present
      const normalizedSlug = record.slug.startsWith("/")
        ? record.slug.slice(1)
        : record.slug;
      return normalizedSlug === currentSegment;
    });

    // If no exact match found, look for a dynamic placeholder
    if (!matchingRecord) {
      matchingRecord = records.find((record) => {
        // Normalize slug for dynamic placeholders too
        const normalizedSlug = record.slug.startsWith("/")
          ? record.slug.slice(1)
          : record.slug;
        return (
          normalizedSlug.startsWith("[") &&
          normalizedSlug.endsWith("]") &&
          !record.hidden // Skip hidden placeholder records unless no other match
        );
      });

      // If still no match and we have hidden records, check them as last resort
      if (!matchingRecord) {
        matchingRecord = records.find((record) => {
          const normalizedSlug = record.slug.startsWith("/")
            ? record.slug.slice(1)
            : record.slug;
          return normalizedSlug.startsWith("[") && normalizedSlug.endsWith("]");
        });
      }
    }

    if (!matchingRecord) {
      return undefined;
    }

    // Get the endpoint for this record
    let endpoint = isString(matchingRecord.endpoint)
      ? matchingRecord.endpoint
      : parentEndpoint;

    // Normalize slug for checking if it's a dynamic placeholder
    const normalizedSlug = matchingRecord.slug.startsWith("/")
      ? matchingRecord.slug.slice(1)
      : matchingRecord.slug;

    // Copy existing replacements
    const currentReplacements = { ...placeholderReplacements };

    // If current slug is a placeholder, add it to replacements
    if (normalizedSlug.startsWith("[") && normalizedSlug.endsWith("]")) {
      // Extract the placeholder name (e.g., "id" from "[id]")
      const placeholderName = normalizedSlug.slice(1, -1);
      currentReplacements[placeholderName] = currentSegment;
    }

    // Apply all placeholder replacements to the endpoint
    if (endpoint) {
      Object.entries(currentReplacements).forEach(([placeholder, value]) => {
        const placeholderPattern = new RegExp(`\\[${placeholder}\\]`, "g");
        endpoint = endpoint?.replace(placeholderPattern, value);
      });
    }

    // If no more segments to process, return the current endpoint
    if (remainingSegments.length === 0) {
      return endpoint;
    }

    // If there are more segments and the record has steps, continue searching
    if (matchingRecord.steps && matchingRecord.steps.length > 0) {
      return findEndpoint(
        matchingRecord.steps as BasNavigationStateRecord[],
        remainingSegments,
        endpoint,
        currentReplacements, // Pass replacements to children
      );
    }

    // No more steps but still have segments - no match
    return undefined;
  };

  return findEndpoint(navigationState, pathSegments);
};
